import { takeLineClickEventId } from "@/lib/line-click";
import { LIFF_LP_ID, LIFF_LP_SLUGS, LP_LINE_URL, LP_SESSION_STORAGE_KEY, isLpSessionId } from "@/lib/lp-line";
import { createContext, useContext, useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { Mail, Video } from "lucide-react";
import { InlineEditProvider } from "@noiman/inline-edit";
import { useNavigate } from "react-router";
import { CounselingCalendar, type CounselingCalendarCopy } from "@/components/CounselingCalendar";
import { readLpAttribution } from "@/lib/attribution";
import { sitePath } from "@/lib/site-path";
import { Laurel } from "@/components/common/Laurel";
import { LogoText } from "@/components/TechChip";
import { captureCounselingEvent } from "@/lib/counseling-analytics";
import { sanitizeAnalyticsUrl } from "../../../../lib/analytics-url";

// ── キャンペーン LP 共通キット (Tailwind 版) ─────────────────────────
// 全セクションを HTML で組み立てる。色は Figma デザインのトーンを踏襲し、
// サイトのテーマ色 (shegate-accent/green/beige/black) と同一系で統一。
export const FONT =
  "'Zen Kaku Gothic New','Noto Sans JP',-apple-system,sans-serif";

// Figma デザインから採取した実測値。tailwind.config.ts の lp.* と同一ソース。
// 各色は var(--lp-*, 既定値) 形式で、既定値は元の実測値のまま。
// 変数を定義しない既存 LP は見た目が一切変わらず、外側の祖先要素で
// --lp-* を上書きした LP だけ配色を差し替えられる (campaign-1 が使用)。
export const C = {
  orange: "var(--lp-orange, #F39800)", // セクション橙
  orangeDeep: "var(--lp-orange-deep, #E08A00)",
  fvStart: "var(--lp-fv-start, #EE8F00)", // FV 橙グラデ上端
  fvEnd: "var(--lp-fv-end, #D78801)", // FV 橙グラデ下端
  blue: "var(--lp-blue, #0117C1)", // CTA 帯の青ボックス
  blueDark: "var(--lp-blue-dark, #0013A8)",
  navy: "var(--lp-navy, #1A3764)", // お悩みカード
  navyDeep: "var(--lp-navy-deep, #0F1533)",
  black: "var(--lp-black, #212121)", // ダークセクション背景
  pitch: "var(--lp-pitch, #000000)", // 料金セクション
  cream: "var(--lp-cream, #FFF3E0)", // フォーム・バンド背景
  creamDark: "var(--lp-cream-dark, #F5E6C8)",
  ink: "var(--lp-ink, #191919)",
  sub: "var(--lp-sub, #4A4A4A)",
  line: "var(--lp-line, #E3E3E3)",
} as const;

// 写真・イラスト素材 (Figma からの切り出し)

// ── 共通テキスト装飾 ────────────────────────────────────────────────
// 白地ボックスに黒太字 (見出し内の強調ワード)
export function WBox({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block bg-white px-3 py-1 align-middle text-black md:px-4">
      {children}
    </span>
  );
}

// 黒地ボックスに白太字
export function BBox({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block bg-black px-3 py-1 align-middle text-white md:px-4">
      {children}
    </span>
  );
}

// 縦書きサイドキャプション (セクション端からはみ出す注釈)
export function SideCaption({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`pointer-events-none absolute font-bold ${className}`}
      style={{
        writingMode: "vertical-rl",
        color: C.orange,
      }}
    >
      {children}
    </span>
  );
}

// 「受講生の声」ラベル (白アイコン + 黒文字)
export function VoiceLabel() {
  return (
    <p className="mb-3 flex items-center gap-2 text-[15px] font-black text-black md:text-[17px]">
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
        <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-2a2 2 0 0 1-2-2z" />
      </svg>
      受講生の声
    </p>
  );
}

// 受講生の声カード (生成り背景)
export function VoiceCard({
  img,
  imgAlt,
  imgW,
  imgH,
  name,
  children,
}: {
  img: string;
  imgAlt: string;
  imgW: number;
  imgH: number;
  name: string;
  children: ReactNode;
}) {
  return (
    <div
      className="flex items-start gap-4 rounded-xl p-5 md:gap-6 md:p-7"
      style={{ backgroundColor: C.cream }}
    >
      <img
        src={img}
        alt={imgAlt}
        width={imgW}
        height={imgH}
        className="h-16 w-16 shrink-0 rounded-full object-cover md:h-20 md:w-20"
        loading="lazy"
      />
      <div className="flex-1">
        <p className="text-[13px] leading-[1.9] text-[#191919] md:text-[15px]">
          {children}
        </p>
        <p className="mt-2 text-right text-[12px] text-[#777] md:text-[13px]">
          {name}
        </p>
      </div>
    </div>
  );
}

// 予約を終えた人向けの友だち追加 (完了ページと同じ)
const LINE_URL = "https://yumesaku.tech/women/line/t/lp-counseling-done";
// LP1〜4 の申込ボタンの行き先。予約後の友だち追加 (LINE_URL) とは別に数える
export { LP_LINE_URL };
// その LINE ボタンの文言。ボタンごとに言い方が割れないよう1か所で持つ
// 届く中身 (何をどの順で学ぶかの18ヶ月の地図) に自社の名前を付けて、ほかのスクールの「学習ロードマップ」と分ける
// 2つに分けて、どの幅でも2行で折れるようにする。「無料」は2行目に入れて1行目を短く保つ
const LP_LINE_CTA_PARTS = ["ゆめさく式18ヶ月勉強法を", "LINEで無料で受け取る"];
export const LP_LINE_CTA_LABEL = LP_LINE_CTA_PARTS.join("");

// LP ごとにボタンの文言と受け取りの3ステップを差し替える入口。LpCanvas の ctaCopy から渡す。渡さない LP は既定のまま
export type LineStep = { head: string; body: string; visual: "line" | "roadmap" | "video" | "mail" };
type CtaCopy = { parts: readonly string[]; steps?: LineStep[]; headline?: string; thumb?: string; thumbLabel?: string; thumbAlt?: string };
const CtaCopyContext = createContext<CtaCopy>({ parts: LP_LINE_CTA_PARTS });

/** 幅が足りないときは語の途中でなく、塊の切れ目で折り返す */
export function LineCtaLabel() {
  const { parts } = useContext(CtaCopyContext);
  return (
    <>
      {parts.map((part) => (
        <span key={part} className="inline-block whitespace-nowrap">{part}</span>
      ))}
    </>
  );
}

// ── Meta Pixel ─────────────────────────────────────────────────────
// 広告の最適化に使う。予約完了 (Schedule) はサーバーからも同じ eventID で送り、
// Meta 側で1件にまとめさせる。
const META_PIXEL_ID = "1575263163473951";

type Fbq = (...args: unknown[]) => void;

export function MetaPixel() {
  useEffect(() => {
    const existing = (window as { fbq?: Fbq }).fbq;
    if (existing) {
      existing("track", "PageView");
      return;
    }
    const script = document.createElement("script");
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${META_PIXEL_ID}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }, []);
  return null;
}

function trackMetaSchedule(eventId: string) {
  (window as { fbq?: Fbq }).fbq?.("track", "Schedule", {}, { eventID: eventId });
}

// 広告クリックの ID。ピクセルが読み終わる前だと _fbc クッキーがまだ無いので、
// URL の fbclid から Meta の決まった形 (fb.1.時刻.fbclid) で作る。
// 無いとサーバーの成果が広告のクリックに結び付かない
function readFbc(): string | undefined {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined;
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

// LP の成果は LINE の友だち追加ボタンを押したこと (Lead)。
// サーバーからも同じ eventID で送り、ブラウザで弾かれた分を補う。
// 送ったらすぐ LINE へ移る。ピクセル本体を待つと最大 2.4 秒かかり、その間に離脱する
// (2026-10-13 オーナー「最速にしないと」)。ブラウザの Lead が移動で切れても、サーバーの Lead は
// keepalive で届く。新しいタブで開く操作はそのまま通す

export function openLineRegister(event: MouseEvent<HTMLAnchorElement>) {
  // 押した瞬間に eventID を受け取る (Pixel を待つ間に次のクリックが来ても、各クリックは自分の ID で送る)。
  // null は連打の2回目で、PostHog の line_click と同じく数えない
  const eventId = takeLineClickEventId();
  const plain = !(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  if (!eventId) {
    if (plain) event.preventDefault();
    return;
  }
  if (!plain) {
    trackLineRegister(eventId);
    return;
  }
  event.preventDefault();
  const href = liffLineHref() ?? event.currentTarget.href;
  trackLineRegister(eventId);
  window.location.assign(href);
}

// LIFF を通す LP では、訪問のセッション ID を付けて LIFF へ送る。
// LIFF のページがその人の LINE ID と対にして残すので、友だち追加を時刻に頼らず訪問に結べる
function liffLineHref(): string | null {
  try {
    // 末尾の / 付きでも同じ LP として扱う
    const slug = window.location.pathname.replace(/\/+$/, "").split("/").pop() ?? "";
    if (!LIFF_LP_SLUGS.includes(slug)) return null;
    const sessionId = window.sessionStorage.getItem(LP_SESSION_STORAGE_KEY);
    if (!isLpSessionId(sessionId)) return null;
    return `https://liff.line.me/${LIFF_LP_ID}?s=${encodeURIComponent(sessionId)}`;
  } catch {
    return null;
  }
}

function trackLineRegister(eventId: string) {
  try {
    (window as { fbq?: Fbq }).fbq?.("track", "Lead", { content_name: "line_register" }, { eventID: eventId });
    void fetch(sitePath("/api/meta-pixel"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: "Lead",
        event_source_url: sanitizeAnalyticsUrl(window.location.href),
        event_id: eventId,
        fbp: readCookie("_fbp"),
        fbc: readFbc(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // 計測の失敗で LINE への移動を止めない
  }
}

// 予約完了より件数の多い「日時を選んだ」。学習用の手前の成果として使う。
// サーバーからも同じ eventID で送り、ブラウザで弾かれた分を補う
function trackMetaSelectSlot() {
  try {
    sendSelectSlot();
  } catch {
    // 計測の失敗で日時の選択を止めない
  }
}

function sendSelectSlot() {
  const eventId = crypto.randomUUID();
  (window as { fbq?: Fbq }).fbq?.("trackCustom", "SelectCounselingSlot", {}, { eventID: eventId });
  void fetch(sitePath("/api/meta-pixel"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_name: "SelectCounselingSlot",
      event_source_url: sanitizeAnalyticsUrl(window.location.href),
      event_id: eventId,
      fbp: readCookie("_fbp"),
      fbc: readFbc(),
    }),
    keepalive: true,
  }).catch(() => {});
}

// ── Canvas ──────────────────────────────────────────────────────────
// Figma デザインは 1024px の中央カラム。広い画面では左右に白余白が出る。
// LINE ボタンの左の絵。face は女性の写真、roadmap は LINE で届くロードマップの縮小画像。
// LpCanvas から渡し、ほかの LP (LpCanvas を使わないページ) は写真のまま
const CtaVisualContext = createContext<"face" | "roadmap">("face");
// 横108px ほどでしか出さないので240px の縮小版を読む。FV の大きな版 (53KB) と別の URL にして2回読まないようにする
const ROADMAP_THUMB = "/assets/lp-meta/c1v2/learning_roadmap_thumb.webp";
// CTA の下に並べる講師4人の顔 (women-parts の TEACHERS と同じ写真)
const TEACHER_FACES = ["yamamoto", "okada", "obi", "ikuta"].map((n) => `/assets/lp-meta/real/teacher-${n}.webp`);

export function LpCanvas({
  children,
  tail,
  editSlug,
  savedHtml,
  vitalsSlug,
  style,
  ctaVisual = "face",
  ctaCopy,
}: {
  children: ReactNode;
  /** 予約フォーム・フッター・StickyCta など React のまま残す末尾要素 */
  tail?: ReactNode;
  /** 指定時は ?mode=edit で本文ダブルクリック編集が有効になる */
  editSlug?: string;
  /** KV に保存された編集済み本文。ある場合は SSR HTML をこちらに置き換える */
  savedHtml?: string | null;
  /** RUM 計測の slug (registry 準拠。省略時は editSlug から lp- 接頭辞を除去) */
  vitalsSlug?: string;
  /** ルート要素へ足すスタイル。--lp-* の上書きをここから全セクションへ継承させる */
  style?: CSSProperties;
  /** LINE ボタンの左に出す絵 */
  ctaVisual?: "face" | "roadmap";
  /** LINE ボタンの文言と受け取りの3ステップを、この LP だけ差し替える */
  ctaCopy?: CtaCopy;
}) {
  return (
    <CtaCopyContext.Provider value={ctaCopy ?? { parts: LP_LINE_CTA_PARTS }}>
    <CtaVisualContext.Provider value={ctaVisual}>
    <div className="bg-white" style={{ fontFamily: FONT, ...style }}>
      {/* LP は広告導線専用。アプリ共通の FAQ チャット起動ボタンは出さない */}
      <style>{"#women-faq-trigger-floating{display:none!important}@keyframes lp-shine{0%,70%{transform:translateX(-120%) skewX(-20deg)}100%{transform:translateX(320%) skewX(-20deg)}}"}</style>
      <div className="mx-auto w-full max-w-[1024px] overflow-hidden bg-white">
        {editSlug ? (
          <InlineEditProvider
            slug={editSlug}
            apiPath={sitePath("/api/inline-edit")}
            className="contents"
          >
            {savedHtml ? (
              // 管理ページで保存された本文を通常閲覧へ反映する。
              // React 管理外の DOM として注入して hydration 衝突を避ける。
              <div dangerouslySetInnerHTML={{ __html: savedHtml }} />
            ) : (
              children
            )}
          </InlineEditProvider>
        ) : (
          children
        )}
        {tail}
      </div>
      <MetaPixel />
      <LpVitalsBeacon
        slug={vitalsSlug ?? editSlug?.replace(/^lp-/, "")}
      />
    </div>
    </CtaVisualContext.Provider>
    </CtaCopyContext.Provider>
  );
}

// ── RUM 速度計測 ────────────────────────────────────────────────────
// LCP/INP/CLS/FCP/TTFB を sendBeacon で /api/lp-vitals へ送る。
// 編集モードでは送らない。失敗しても何もしない (計測は黙って捨てる)。
export function LpVitalsBeacon({ slug }: { slug?: string }) {
  useEffect(() => {
    if (!slug || typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("mode") === "edit")
      return;
    const device = window.matchMedia("(max-width: 768px)").matches
      ? "mobile"
      : "desktop";
    const send = (metric: string, value: number) => {
      try {
        const payload = JSON.stringify({ slug, metric, value, device });
        navigator.sendBeacon(
          sitePath("/api/lp-vitals"),
          new Blob([payload], { type: "application/json" }),
        );
      } catch {
        // 計測失敗は握り潰す (ユーザー体験に影響させない)
      }
    };
    import("web-vitals")
      .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
        onCLS((m) => send("CLS", m.value));
        onFCP((m) => send("FCP", m.value));
        onINP((m) => send("INP", m.value));
        onLCP((m) => send("LCP", m.value));
        onTTFB((m) => send("TTFB", m.value));
      })
      .catch(() => {});
  }, [slug]);
  return null;
}

// ── CTA ボタン ──────────────────────────────────────────────────────
export function CtaButton({
  ctaId,
  className = "",
  style,
  children,
}: {
  ctaId: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <a
      href={LP_LINE_URL}
      onClick={openLineRegister}
      data-cta="true"
      data-cta-id={ctaId}
      className={`inline-block rounded-full text-center font-bold transition-transform hover:scale-[1.02] ${className}`}
      style={style}
    >
      {children}
    </a>
  );
}

// ── LINE CTA カード ─────────────────────────────────────────────────
// 見出しの吹き出し + 写真 + 文言 + 矢印の丸。帯・ページ下・スマホ下部の固定ボタンで同じ形を使う。
// compact は固定ボタン用で、余白と字を詰める
export function LineCtaCard({
  ctaId,
  headline = "採用担当者や現役エンジニアが絶賛！",
  bandImg = "/assets/lp-meta/c1v2/band_woman.webp",
  compact = false,
}: {
  ctaId: string;
  headline?: string;
  bandImg?: string;
  compact?: boolean;
}) {
  const visual = useContext(CtaVisualContext);
  const { parts, headline: copyHeadline, thumb, thumbLabel } = useContext(CtaCopyContext);
  return (
    <>
    <a
      href={LP_LINE_URL}
      onClick={openLineRegister}
      data-cta="true"
      data-cta-id={ctaId}
      aria-label={parts.join("")}
      // 下辺を厚くして「押せる板」に見せ、押している間は沈める (影は使わない)
      className={`relative flex items-center gap-2.5 rounded-xl border-b-[5px] transition-[transform,border-width] duration-75 active:translate-y-[3px] active:border-b-2 ${
        compact ? "px-3 pb-2 pt-4" : "mt-5 px-3 pb-4 pt-6 md:gap-6 md:px-8 md:pb-5 md:pt-7"
      }`}
      style={{
        backgroundColor: "var(--lp-cta-bg, #0117C1)",
        borderBottomColor: "var(--lp-cta-edge, rgba(0,0,0,0.28))",
        // 光は LP が --lp-cta-glow を渡したときだけ (LP1)。ほかの LP は影なしのまま
        boxShadow: "var(--lp-cta-glow, none)",
      }}
    >
      <span
        className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border-2 bg-white px-3 py-1 font-black ${
          compact
            ? "-top-3 text-[12px]"
            : "-top-4 text-[12px] md:-top-5 md:px-5 md:py-1.5 md:text-[14px]"
        }`}
        style={{
          borderColor: "var(--lp-cta-pill, #0117C1)",
          color: "var(--lp-cta-pill, #0117C1)",
        }}
      >
        {copyHeadline ?? headline}
      </span>
      {/* 光の筋がときどき流れて、目をボタンに戻す。動きを控える設定の人には出さない */}
      <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl motion-reduce:hidden">
        <span
          className="absolute inset-y-0 left-0 w-1/4 bg-white/45"
          style={{ animation: "lp-shine 3.6s ease-in-out infinite" }}
        />
      </span>
      {visual === "roadmap" ? (
        // 受け取るものそのものを見せる。少し傾けて「届く1枚」に見せ、角の札で無料だと伝える
        // 画像はボタンの上下にはみ出させて大きく見せる。横幅は文字に回す
        <span className={`relative shrink-0 -rotate-3 ${compact ? "-my-2" : "-my-5 -ml-1 md:my-0 md:ml-0"}`}>
          <img
            src={thumb ?? ROADMAP_THUMB}
            alt=""
            width={960}
            height={540}
            className={`block aspect-[16/9] rounded-md border-2 border-white object-cover object-top ${
              compact ? "w-[72px]" : "w-[80px] min-[375px]:w-[108px] md:w-[170px]"
            }`}
            loading="lazy"
          />
          <span
            className={`absolute -left-1.5 -top-2 rounded-sm px-1 font-black leading-tight text-white ${
              compact ? "text-[12px]" : "text-[12px] md:text-[12px]"
            }`}
            style={{ backgroundColor: "#E5302A" }}
          >
            {thumbLabel ?? "無料"}
          </span>
        </span>
      ) : (
        <img
          src={bandImg}
          alt=""
          width={170}
          height={160}
          className={`shrink-0 self-end rounded-bl-lg object-cover object-bottom ${
            compact ? "-mb-2 w-[44px]" : "-mb-4 w-[64px] md:-mb-5 md:w-[96px]"
          }`}
          loading="lazy"
        />
      )}
      <span className="flex-1 text-center">
        <span
          className={`block font-black text-white ${
            compact
              ? "text-[12px] leading-tight min-[375px]:text-[14px]"
              : "text-[12px] min-[375px]:text-[14px] md:text-[24px]"
          }`}
          style={{ color: "var(--lp-cta-ink, #FFFFFF)" }}
        >
          <LineCtaLabel />
        </span>
      </span>
      <span
        className={`flex shrink-0 items-center justify-center rounded-full bg-black/30 ${
          compact ? "h-7 w-7" : "h-7 w-7 min-[375px]:h-8 min-[375px]:w-8 md:h-12 md:w-12"
        }`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          className={`text-white ${compact ? "" : "md:h-6 md:w-6"}`}
        >
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </a>
    {/* 誰が教えるかを顔で見せ、押したあとの手間も先に言う。固定ボタンは高さが無いので出さない */}
    {compact ? null : (
      <div className="mt-2.5 flex items-center justify-center gap-2" style={{ color: "var(--lp-ink, #252525)" }}>
        <span className="flex -space-x-2" aria-hidden="true">
          {TEACHER_FACES.map((src) => (
            <img key={src} src={src} alt="" width={240} height={240} className="h-7 w-7 rounded-full border-2 border-white object-cover" loading="lazy" />
          ))}
        </span>
        <span className="text-left text-[12px] font-bold leading-snug md:text-[13px]">
          講師の一部をご紹介します
          <br />
          <span className="font-normal">LINEで友だち追加するだけ・登録は無料</span>
        </span>
      </div>
    )}
    </>
  );
}

// ── CTA バンド ──────────────────────────────────────────────────────
// Figma: 白地に薄い斜線ストライプ、✦キャプション、青ボックス内に
// 人物写真 + 白ピル帯 + 申し込みテキスト + 下向き矢印サークル。
export function CtaBand({
  ctaId,
  variant = "stripe",
  caption,
  headline = "自分に最適な英語学習法がわかる！",
  bandImg = "/assets/lp-meta/c1v2/band_woman.webp",
  proof,
}: {
  ctaId: string;
  variant?: "stripe" | "blue";
  caption?: ReactNode;
  headline?: string;
  bandImg?: string;
  proof?: ReactNode;
}) {
  if (variant === "blue") {
    return (
      <section
        className="py-10 md:py-14"
        style={{ backgroundColor: "var(--lp-cta-bg, #0117C1)" }}
      >
        <div className="mx-auto max-w-[640px] px-6 text-center">
          <CtaButton
            ctaId={ctaId}
            className="w-full bg-white px-8 py-5 text-[18px] md:text-[22px] shadow-[0_6px_20px_rgba(0,0,0,0.25)]"
          >
            <span style={{ color: "var(--lp-cta-pill, #0117C1)" }}>
              <span className="block text-[12px] md:text-[13px] font-bold tracking-wide">
                〈友だち追加するだけ〉
              </span>
              <LineCtaLabel />
            </span>
          </CtaButton>
        </div>
      </section>
    );
  }
  return (
    <section
      className="py-8 md:py-10"
      style={{
        backgroundColor: "var(--lp-band-bg, #FFFFFF)",
        backgroundImage:
          "var(--lp-band-stripe, repeating-linear-gradient(135deg, #FBF7EC 0 8px, transparent 8px 20px))",
      }}
    >
      <p className="text-center text-[16px] md:text-[19px] font-black tracking-wide">
        {caption ?? (
          <>
            <span style={{ color: C.orange }}>✦対面✦</span>
            でも
            <span style={{ color: C.orange }}>✦オンライン✦</span>
            でも可能
          </>
        )}
      </p>
      <div className="mx-auto mt-5 max-w-[760px] px-4 md:px-6">
        {proof ? <div className="mt-4 flex justify-center">{proof}</div> : null}
        <LineCtaCard ctaId={ctaId} headline={headline} bandImg={bandImg} />
      </div>
    </section>
  );
}

// ── 見出し部品 ──────────────────────────────────────────────────────
export function SecKicker({
  children,
  dark = false,
}: {
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <span
      className={`inline-block px-4 py-1.5 text-[13px] md:text-[14px] font-bold ${
        dark ? "bg-black text-white" : "bg-white text-black"
      }`}
    >
      {children}
    </span>
  );
}

// ── Booking form ────────────────────────────────────────────────────
type BookingFormData = {
  date: string;
  time: string;
  name: string;
  email: string;
  age: string;
  occupation: string;
  experience: string;
  experienceDetail: string;
  timeline: string;
  message: string;
};

function BookingFormInner({ copy }: { copy?: CounselingCalendarCopy } = {}) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [notificationWarning, setNotificationWarning] = useState("");

  const handleSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    setError(null);
    captureCounselingEvent("counseling_form_submit", {
      notification_channel: "email",
    });
    try {
      const attribution = readLpAttribution();
      const metaEventId = crypto.randomUUID();
      const response = await fetch(sitePath("/api/counseling"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ...attribution, meta_event_id: metaEventId }),
      });
      const result = (await response.json()) as {
        success?: boolean;
        error?: string;
        notificationSent?: boolean;
      };
      if (!result.success) {
        throw new Error(result.error || "予約に失敗しました");
      }
      if (result.notificationSent === false) {
        setNotificationWarning(
          "予約は完了しましたが、確認メールを送信できませんでした。お手数ですが、下のボタンから公式LINEを友だち追加し、「お名前」と「予約完了」をお送りください。",
        );
      }
      setSuccess(true);
      trackMetaSchedule(metaEventId);
      captureCounselingEvent("counseling_application_complete", {
        notification_channel: "email",
      });
      // 完了ページで LINE 登録を案内する。確認メールが送れなかったときは、
      // その注意をこの場で見せたいので移らない。名前は URL に載せない
      if (result.notificationSent !== false) {
        navigate(
          `/lp/counseling/complete?date=${encodeURIComponent(data.date)}&time=${encodeURIComponent(data.time)}`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "予約に失敗しました");
      captureCounselingEvent("counseling_form_error", {
        notification_channel: "email",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        className="bg-white rounded-lg border border-gray-200 p-8 text-center"
        data-counseling-success="true"
      >
        <p
          className="text-[20px] font-bold mb-3"
          style={{ color: C.ink, fontFamily: FONT }}
        >
          予約が完了しました
        </p>
        <p
          className="text-[14px] leading-[1.9]"
          style={{ color: C.sub, fontFamily: FONT }}
        >
          {notificationWarning || (
            <>
              詳細はメールでお送りします。
              <br />
              当日お会いできることを楽しみにしています。
            </>
          )}
        </p>
        {notificationWarning && (
          <a
            href={LINE_URL}
            className="mt-5 inline-block rounded-full px-6 py-3 text-[15px] font-bold text-white"
            style={{ backgroundColor: "#06C755", fontFamily: FONT }}
          >
            公式LINEで問い合わせる
          </a>
        )}
      </div>
    );
  }
  return (
    <>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p
            className="text-[14px] text-red-600 text-center"
            style={{ fontFamily: FONT }}
          >
            {error}
          </p>
        </div>
      )}
      <CounselingCalendar
        onSubmit={handleSubmit}
        onFirstSlotSelect={trackMetaSelectSlot}
        isSubmitting={isSubmitting}
        variant="green"
        copy={copy}
      />
    </>
  );
}

// ── 予約セクション (#booking) ────────────────────────────────────────
// Figma: 生成り背景 +「無料カウンセリングフォーム」見出し +
// 橙枠の特典ボックス (校舎カウンセリング限定・書籍プレゼント) + フォーム。
export function BookingSection({
  badge = "校舎カウンセリング限定",
  offerLead = "プログリットの学習メソッドが詰まった書籍",
  offerHighlight = "「英語学習2.0」",
  offerEnd = "をプレゼント中！",
  formCopy,
  // 既定は自社の面談画面。以前は制作時に参考にした他社の書籍画像を指していた
  offerImg = {
    src: "/assets/lp-meta/c1v2/one_on_one.webp",
    alt: "オンライン面談の画面",
    width: 1200,
    height: 750,
    className: "w-[120px] shrink-0 rounded md:w-[220px]",
  },
}: {
  badge?: string;
  offerLead?: string;
  offerHighlight?: string;
  offerEnd?: string;
  formCopy?: CounselingCalendarCopy;
  offerImg?: { src: string; alt: string; width: number; height: number; className?: string };
} = {}) {
  return (
    <section id="booking" style={{ backgroundColor: C.cream }}>
      <div className="mx-auto max-w-[823px] px-5 py-12 md:py-16">
        <h2
          className="text-center text-[22px] md:text-[28px] font-black tracking-wide"
          style={{ color: C.ink }}
        >
          無料カウンセリングフォーム
        </h2>
        <div
          className="mt-6 flex items-center gap-4 rounded-xl border-2 bg-white p-4 md:gap-6 md:p-6"
          style={{ borderColor: "var(--lp-offer-border, #F39800)" }}
        >
          <div className="flex-1">
            <p
              className="inline-block rounded px-3 py-1 text-[12px] font-black text-white md:text-[13px]"
              style={{
                backgroundColor: "var(--lp-badge-bg, #F39800)",
                color: "var(--lp-badge-ink, #FFFFFF)",
              }}
            >
              {badge}
            </p>
            <p className="mt-2 text-[14px] font-bold leading-relaxed md:text-[16px]">
              {offerLead}
              <br />
              <span style={{ color: C.orange }}>{offerHighlight}</span>
              {offerEnd}
            </p>
          </div>
          <img
            src={offerImg.src}
            alt={offerImg.alt}
            width={offerImg.width}
            height={offerImg.height}
            className={offerImg.className ?? "w-[64px] shrink-0 md:w-[110px]"}
            loading="lazy"
          />
        </div>
        <div className="mt-8">
          <BookingFormInner copy={formCopy} />
        </div>
      </div>
    </section>
  );
}

/**
 * LINE 登録の3段 (番号、見出しと一文、各段の絵)。LineRegisterSection と、30日の案内ページ (30days-challenge) が同じものを使う。
 * 番号の色は --lp-step-bg と --lp-step-ink で決まる。LP の外 (HP の見た目のページ) で使うときは stepBg と stepInk を渡す
 */
export function LineStepList({
  steps,
  thumb,
  thumbAlt,
  stepBg = "var(--lp-step-bg, var(--lp-cta-bg))",
  stepInk = "var(--lp-step-ink, var(--lp-cta-ink))",
}: {
  steps: LineStep[];
  thumb?: string;
  thumbAlt?: string;
  stepBg?: string;
  stepInk?: string;
}) {
  return (
    <ol className="mt-7 grid gap-3">
      {steps.map((step, i) => (
        <li
          key={step.head}
          className="flex items-start gap-4 rounded-xl border bg-white p-4 md:p-5"
          style={{ borderColor: "#E0DDD6" }}
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[15px] font-black"
            style={{ backgroundColor: stepBg, color: stepInk }}
          >
            {i + 1}
          </span>
          <span className="flex-1">
            <b className="block text-[15px] font-black md:text-[18px]" style={{ color: C.ink }}>
              {step.head}
            </b>
            <span className="mt-1 block text-[13px] leading-relaxed md:text-[14px]" style={{ color: C.sub }}>
              <LogoText text={step.body} />
            </span>
          </span>
          {/* 各段で何が起きるかを絵でも見せる */}
          {step.visual === "roadmap" ? (
            <img
              src={thumb ?? ROADMAP_THUMB}
              alt={thumb ? (thumbAlt ?? "") : "届く学習ロードマップ"}
              width={960}
              height={540}
              className="w-[68px] shrink-0 self-center rounded-md border md:w-[120px]"
              style={{ borderColor: "#E0DDD6" }}
              loading="lazy"
            />
          ) : step.visual === "line" ? (
            // LINE の公式のアプリアイコンの形 (simple-icons の line.svg に公式色 #06C755 を塗ったもの)
            <img
              src="/assets/lp-meta/app/line-icon.svg"
              alt="LINE"
              width={24}
              height={24}
              className="h-12 w-12 shrink-0 self-center"
              loading="lazy"
            />
          ) : (
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center self-center rounded-full"
              style={{ backgroundColor: "#F3F1EC" }}
              aria-hidden="true"
            >
              {step.visual === "mail" ? <Mail size={24} strokeWidth={1.8} color="#252525" /> : <Video size={24} strokeWidth={1.8} color="#252525" />}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

// ── LINE 登録セクション (#booking) ───────────────────────────────────
// 広告 LP campaign-1〜4 の末尾。予約フォームの代わりに LINE の友だち追加へ送る。
// id は予約フォームと同じ booking のまま残す。区間の計測と StickyCta の出し分けがこの id を見ている
// lead を渡すと、印・見出し・一文の代わりにそれを出す。background と一緒に使い、特典の中身とこの節を1つにまとめる (LP1)
export function LineRegisterSection({ proof, heading, lead, background }: { proof?: ReactNode; heading?: ReactNode; lead?: ReactNode; background?: string } = {}) {
  const { steps: lineSteps, thumb, thumbAlt } = useContext(CtaCopyContext);
  return (
    <section id="booking" style={{ background: background ?? C.cream }}>
      <div className="mx-auto max-w-[640px] px-4 py-12 md:px-5 md:py-16">
        {lead ?? (
        <>
        {/* ゆめさく式の印。受け取るものの名前を、見出しの前に目で覚えてもらう */}
        <img
          src="/assets/lp-meta/app/yumesaku-method-badge.webp"
          alt="ゆめさく式 18ヶ月勉強法"
          width={220}
          height={220}
          className="mx-auto mb-3 w-[110px] md:w-[130px]"
          loading="lazy"
        />
        {heading ?? (
        <h2
          className="text-center text-[19px] min-[375px]:text-[22px] md:text-[28px] font-black leading-snug tracking-wide"
          style={{ color: C.ink }}
        >
          何から始めるか、
          <br />
          これでわかる。
        </h2>
        )}
        <p className="mt-2 text-center text-[13px] font-bold md:text-[15px]" style={{ color: C.sub }}>
          ゆめさく式18ヶ月勉強法を、LINEで無料で
        </p>
        </>
        )}
        <LineStepList steps={lineSteps ?? LINE_STEPS} thumb={thumb} thumbAlt={thumbAlt} />
        {/* 色のついた地の上では、受取者数の札とボタンを白い面に載せて字を読めるようにする */}
        <div className={`mx-auto mt-6 max-w-[760px] ${background ? "-mx-2 rounded-2xl bg-white pb-4 pt-1" : ""}`}>
          {proof ? <div className="mt-4 flex justify-center">{proof}</div> : null}
          <LineCtaCard ctaId="booking-line" />
          {/* 受取者数の札の ※1 の説明。FV に説明が無い LP でも、ここで必ず読めるようにする */}
          {proof ? (
            <p className="mt-3 text-center text-[12px] leading-snug md:text-[12px]" style={{ color: C.sub }}>
              ※1 LINEで学習ロードマップを受け取った方の累計（2026年9月時点）
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// 押したあとの流れ。カウンセリングは LINE に入ったあとの次の段で、申し込みの条件にしない
const LINE_STEPS: LineStep[] = [
  { head: "LINEで友だち追加", body: "ボタンを押すと LINE が開きます。追加は無料です。", visual: "line" },
  { head: "勉強法が届く", body: "ゆめさく式18ヶ月勉強法。何をどの順に学ぶかがわかります。", visual: "roadmap" },
  { head: "希望する方は無料相談へ", body: "オンラインで60分。学び方を一緒に整理します。", visual: "video" },
];

// ── Footer ──────────────────────────────────────────────────────────
// LP 共通フッター。本文は PROGRIT 文言を踏襲するが、名義だけは
// 運営元 (ゆめさくエンジニア / 株式会社BlueAI) のものに統一する。
// laurelColor は月桂樹の色。渡さなければ金のまま (開発演習の LP は姉妹 LP の見た目に合わせて青を渡す)
/** showLaurel を false にすると、月桂樹の名乗り (18ヶ月の実践スクール) を出さない。名乗りを置かないページ用 */
export function LpFooter({ laurelColor, showLaurel = true }: { laurelColor?: string; showLaurel?: boolean } = {}) {
  const year = new Date().getFullYear();
  return (
    <footer id="lp-footer" className="bg-white" style={{ color: C.ink }}>
      <div className="mx-auto max-w-[1024px] px-6 pt-6">
        <img
          src="/assets/yumesaku_enginner_logo.svg"
          alt="ゆめさくエンジニア"
          width={968}
          height={175}
          className="w-[120px] md:w-[160px]"
        />
        {/* 最後に読まれる一言。10/8 に決めた看板の言葉で、広告の署名の上の2行とそろえる */}
        <p className="mt-4 font-black leading-snug" style={{ color: C.ink }}>
          <span className="block text-[20px] md:text-[28px]">
            <span className="inline-block">在宅リモートで働ける</span>
            <span className="inline-block" style={{ color: "var(--lp-orange, #EA5504)" }}>エンジニアへ。</span>
          </span>
          <span className="mt-1 block text-[12px] md:text-[14px]">
            <span className="inline-block">AIとエンジニアリングを、要件定義・設計まで。</span>
            <span className="inline-block">18ヶ月の次世代ITスクール。</span>
          </span>
          {/* 学校の名乗りを月桂樹で囲み、実績の札と同じ格に見せる */}
          {showLaurel && (
            <span className="mt-3 inline-flex items-center gap-1" style={{ color: C.ink }}>
              <Laurel height={40} color={laurelColor} />
              <span className="text-center text-[12px] font-black leading-snug md:text-[14px]">
                AIとエンジニアスキルを学べる
                <br />
                18ヶ月の実践スクール
              </span>
              <Laurel height={40} flip color={laurelColor} />
            </span>
          )}
        </p>
      </div>
      <nav className="mx-auto flex max-w-[1024px] flex-wrap gap-x-4 px-6 pt-3 text-[12px] md:text-[13px] [&>a]:inline-flex [&>a]:min-h-[44px] [&>a]:items-center">
        <a href={sitePath("/tokushoho")} className="hover:underline">
          特定商取引法に基づく表記
        </a>
        <a href={sitePath("/privacy")} className="hover:underline">
          プライバシーポリシー
        </a>
        <a href={sitePath("/terms")} className="hover:underline">
          利用規約
        </a>
        <a href={sitePath("/contact")} className="hover:underline">
          お問い合わせ
        </a>
      </nav>
      <p className="mx-auto max-w-[1024px] px-6 pb-5 pt-2 text-[12px] text-[#666]">
        &copy; {year} ゆめさくエンジニア. All rights reserved.
      </p>
    </footer>
  );
}

// ── Sticky mobile CTA ────────────────────────────────────────────────
/**
 * flat は影を付けず上に罫線を引く。影を使わない v2 の LP 用。
 * fromTop は FV の時点から出す。サーバーの HTML にも入るので JS を待たずに見える
 */
export function StickyCta({ flat = false, fromTop = false, headline, bandImg, proof }: { flat?: boolean; fromTop?: boolean; headline?: string; bandImg?: string; proof?: ReactNode } = {}) {
  const [visible, setVisible] = useState(fromTop);
  useEffect(() => {
    const onScroll = () => {
      const fv = document.getElementById("section-fv");
      const booking = document.getElementById("booking");
      const footer = document.getElementById("lp-footer");
      if (!fv) return;
      const fvOut = fromTop || fv.getBoundingClientRect().bottom <= 0;
      const bookingVisible = booking
        ? booking.getBoundingClientRect().top < window.innerHeight &&
          booking.getBoundingClientRect().bottom > 0
        : false;
      // フッターが見えたら引っ込める。これでフッターがバーの下に隠れず、下の余白も要らない
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      setVisible(fvOut && !bookingVisible && !footerVisible);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [fromTop]);

  if (!visible) return null;
  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-2 pt-2 md:hidden"
        style={{
          background: "rgba(255,255,255,0.96)",
          ...(flat
            ? { borderTop: "1px solid #E0DDD6" }
            : { boxShadow: "0 -4px 20px rgba(0,0,0,0.12)" }),
        }}
      >
        {proof ? <div className="mb-4 flex justify-center">{proof}</div> : null}
        <LineCtaCard ctaId="sticky" headline={headline} bandImg={bandImg} compact />
      </div>
    </>
  );
}
